import { useState, useCallback } from "react";
import { NodeViewWrapper, type NodeViewProps } from "@tiptap/react";
import { BlockEditModal } from "../BlockEditModal";
import styles from "../blocks/blocks.module.scss";

export function CoverNodeView({ node, updateAttributes, selected, deleteNode }: NodeViewProps) {
  const src = (node.attrs.src as string) || "";
  const title = (node.attrs.title as string) || "";
  const subtitle = (node.attrs.subtitle as string) || "";
  const author = (node.attrs.author as string) || "";

  const [isOpen, setIsOpen] = useState(false);
  const [draftSrc, setDraftSrc] = useState(src);
  const [draftTitle, setDraftTitle] = useState(title);
  const [draftSubtitle, setDraftSubtitle] = useState(subtitle);
  const [draftAuthor, setDraftAuthor] = useState(author);

  const handleOpen = useCallback(() => {
    setDraftSrc(src);
    setDraftTitle(title);
    setDraftSubtitle(subtitle);
    setDraftAuthor(author);
    setIsOpen(true);
  }, [src, title, subtitle, author]);

  const handleSave = useCallback(() => {
    updateAttributes({
      src: draftSrc,
      title: draftTitle,
      subtitle: draftSubtitle,
      author: draftAuthor,
    });
    setIsOpen(false);
  }, [draftSrc, draftTitle, draftSubtitle, draftAuthor, updateAttributes]);

  const hasCover = src || title || subtitle || author;

  return (
    <NodeViewWrapper data-type="cover-block" data-block-id={node.attrs.blockId} className={selected ? "ProseMirror-selectednode" : ""}>
      <div
        className={styles.coverBlock}
        onClick={handleOpen}
        style={{ cursor: "pointer" }}
        title="Click to edit cover"
      >
        {hasCover ? (
          <>
            {src && (
              <div className={styles.coverImageWrapper}>
                <img src={src} alt={title || "Cover"} className={styles.coverImage} />
              </div>
            )}
            {title && <div className={styles.coverTitle}>{title}</div>}
            {subtitle && <div className={styles.coverSubtitle}>{subtitle}</div>}
            {author && <div className={styles.coverAuthor}>{author}</div>}
          </>
        ) : (
          <div className={styles.coverPlaceholder}>
            <span style={{ fontSize: 14, color: "var(--lilia-text-muted, #999)" }}>Click to set up cover page</span>
          </div>
        )}
      </div>

      <BlockEditModal
        sourceNode={node}
        title="Edit Cover"
        open={isOpen}
        onClose={() => setIsOpen(false)}
        onSave={handleSave}
        onDelete={() => { setIsOpen(false); deleteNode(); }}
        width={640}
      >
        <div style={{ marginBottom: 16 }}>
          <label style={{ display: "block", fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Cover Image URL</label>
          <input
            type="text"
            value={draftSrc}
            onChange={(e) => setDraftSrc(e.target.value)}
            placeholder="https://example.com/cover.jpg"
            style={{ width: "100%", padding: "8px 12px", fontSize: 13, border: "1px solid var(--lilia-border, #ddd)", borderRadius: 4 }}
          />
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={{ display: "block", fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Title</label>
          <input
            type="text"
            value={draftTitle}
            onChange={(e) => setDraftTitle(e.target.value)}
            placeholder="Book Title"
            style={{ width: "100%", padding: "8px 12px", fontSize: 13, border: "1px solid var(--lilia-border, #ddd)", borderRadius: 4 }}
          />
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={{ display: "block", fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Subtitle (optional)</label>
          <input
            type="text"
            value={draftSubtitle}
            onChange={(e) => setDraftSubtitle(e.target.value)}
            placeholder="A Subtitle"
            style={{ width: "100%", padding: "8px 12px", fontSize: 13, border: "1px solid var(--lilia-border, #ddd)", borderRadius: 4 }}
          />
        </div>

        <div>
          <label style={{ display: "block", fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Author</label>
          <input
            type="text"
            value={draftAuthor}
            onChange={(e) => setDraftAuthor(e.target.value)}
            placeholder="Author Name"
            style={{ width: "100%", padding: "8px 12px", fontSize: 13, border: "1px solid var(--lilia-border, #ddd)", borderRadius: 4 }}
          />
        </div>
      </BlockEditModal>
    </NodeViewWrapper>
  );
}
